/**
 * Telephony client — singleton TelnyxRTC wrapped in a small, typed API.
 *
 * Responsibilities:
 *  - Own the TelnyxRTC instance for the life of the tab (beforeunload cleanup).
 *  - Authenticate via JWT login_token from /api/telephony/token and refresh at
 *    ~7h (TTL 8h), so long sessions never drop auth mid-call.
 *  - Translate SDK notifications into XState events via the call registry.
 *  - Subscribe to Supabase Realtime for the `calls` table and enrich inbound
 *    records with DB data (callId, conversationId, customer, brand).
 *
 * Multi-tab model: exactly one tab (the "owner", elected via the Web Locks API
 * in `leader.ts`) connects + holds the audio. Followers never connect — they
 * mirror the owner's state and proxy commands over the phone-bus. So the
 * connect/Realtime/SDK code below only ever runs on the owner; command
 * functions are leader-aware (follower → bus, owner → execute locally).
 *
 * Everything is module-scoped; React never holds a reference to the SDK
 * instance. Hooks read the uniform mirror in `phone-state.ts`.
 */

import { TelnyxRTC } from '@telnyx/webrtc';
import type { RealtimeChannel } from '@supabase/supabase-js';

import { supabase } from '../supabase';
import { emit, setEventUser, subscribe as subscribeEvents } from './events';
import * as registry from './call-registry';
import {
  ensureRemoteAudioElement,
  applyRemoteSpeakerSink,
  clearRemoteAudio,
  getRemoteAudioHealth,
  tryResumeRemoteAudio,
} from './audio';
import { initToneDriver } from './tone-driver';
import { startSampling } from './telemetry';
import {
  applyCachePatch,
  loadPreferences,
  savePreferences,
  type DevicePreferences,
} from './devices';
import { showIncoming } from './notifications';
import * as pendingOutbound from './pending-outbound';
import { acquireLeadership, isLeader } from './leader';
import { getPresenceState, initPresenceStore } from './presence-store';
import * as phoneBus from './phone-bus';
import type { BusMessage, CallError, SerializableStats } from './phone-bus';
import * as phoneState from './phone-state';
import { sdkCalls, finalizeCall, setOnFinalized, resetLifecycle } from './call-lifecycle';
import { OWN_DIDS_DIGITS } from './own-dids';
import {
  assessOutboundEnd,
  decideSuspectAction,
  noticeFor,
  INVITE_WAIT_MS,
  SERVER_TERMINAL_GRACE_MS,
} from './registration-watch';
import type { SuspectReason } from './registration-watch';
import type {
  EventSource as TelephonyEventSource,
  Brand,
  CallDirection,
  CallerInfo,
  CallRecord,
  ClientCallId,
  PhoneStatus,
  TransferStateInfo,
  DialContext,
} from './types';

// ─── Module state ──────────────────────────────────────────────────

let sharedClient: TelnyxRTC | null = null;

// Regionale RTC-host (wss://eu.rtc.telnyx.com) in plaats van de wereldwijde
// rtc.telnyx.com. De wereldwijde naam geeft per DNS-vraag ÉÉN IP uit een
// gewogen pool; staat daar een dode node in (12-09-2026 22:10: 185.246.41.166
// beantwoordde geen TCP meer, maar bleef ~80% van de antwoorden), dan wacht de
// browser de volle handshake-timeout (~20s) op dat ene IP en registreert de
// softphone nooit. De regionale naam geeft de hele pool (4 A-records) terug,
// zodat de browser bij een dode node zelf doorschakelt naar de volgende.
// VITE_TELNYX_RTC_REGION: 'eu' (default), 'us-east', 'us-west' of 'global'
// (= SDK-default rtc.telnyx.com, de oude situatie).
const RTC_REGION_RAW = (import.meta.env.VITE_TELNYX_RTC_REGION as string | undefined)?.trim();
const RTC_REGION: string | undefined =
  RTC_REGION_RAW === 'global' ? undefined : RTC_REGION_RAW || 'eu';
let connectPromise: Promise<void> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let realtimeChannel: RealtimeChannel | null = null;
let phoneStatus: PhoneStatus = 'disconnected';
let userId: string | null = null;
// Wanneer het huidige login-token verloopt. checkConnectionHealth gebruikt
// dit om zombie-registraties te herkennen: socket "connected" maar de
// registratie bij Telnyx is server-side al verlopen (incident 2026-07-13:
// medewerker-tab 3u "geregistreerd" volgens de SDK, answer_failed
// no_sdk_call bij elke inkomende call).
let tokenExpiresAtMs: number | null = null;

// Reconnect-state (owner-only): backoff + wake/online-detectie.
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let reconnecting = false;
let wakeTimer: ReturnType<typeof setInterval> | null = null;
let onlineListener: (() => void) | null = null;
let visibilityListener: (() => void) | null = null;
let healthRetryTimer: ReturnType<typeof setTimeout> | null = null;
let realtimeBackoff = 0;

// ─── Status helpers ────────────────────────────────────────────────

type StatusListener = (s: PhoneStatus) => void;
const statusListeners = new Set<StatusListener>();

export function getStatus(): PhoneStatus {
  return phoneStatus;
}

export function subscribeStatus(l: StatusListener): () => void {
  statusListeners.add(l);
  return () => {
    statusListeners.delete(l);
  };
}

function setStatus(next: PhoneStatus): void {
  if (phoneStatus === next) return;
  phoneStatus = next;
  statusListeners.forEach((l) => {
    try {
      l(next);
    } catch (err) {
      console.error('[telephony/client] status listener error', err);
    }
  });
  publishSnapshot();
}

// ─── Mute state (reflects SDK truth back to UI subscribers) ────────

type MuteListener = (muted: boolean) => void;
const muteListeners = new Set<MuteListener>();
let isMutedState = false;

export function isMuted(): boolean {
  return isMutedState;
}

export function subscribeMute(l: MuteListener): () => void {
  muteListeners.add(l);
  return () => {
    muteListeners.delete(l);
  };
}

function setMutedState(muted: boolean): void {
  if (isMutedState === muted) return;
  isMutedState = muted;
  muteListeners.forEach((l) => {
    try {
      l(muted);
    } catch (err) {
      console.error('[telephony/client] mute listener error', err);
    }
  });
  publishSnapshot();
}

// ─── Ownership: snapshot publishing + command proxy ────────────────
//
// Leadership itself lives in leader.ts (Web Locks). Here we (a) project the
// owner's registry/status/mute/disposition into a serialisable snapshot for
// the mirror, and (b) execute commands proxied from follower tabs. None of
// this does anything on a follower (publish + command handling are gated on
// isLeader()).

// The last completed (answered) call, awaiting disposition. Kept after the
// registry entry is removed so the disposition form survives cleanup.
let dispositionRecord: CallRecord | null = null;
// Compact quality of the active call, mirrored to followers.
let lastStats: SerializableStats | null = null;
// Laatste call-fout (bv. aannemen mislukt) — zichtbaar op elke tab.
let lastCallError: CallError | null = null;
// Melding buiten een gesprek (pil), bv. "verbinding stil weggevallen en hersteld".
let lastNotice: string | null = null;
let noticeTimer: ReturnType<typeof setTimeout> | null = null;

// Registratie-bewaker (registration-watch.ts): bijhouden wanneer de browser een
// uitgaande A-leg opnam, welke inkomende seeds nog op hun SDK-invite wachten,
// en welke snelle SDK-vallen nog op de server-terminal wachten.
const bridgeAnsweredAt = new Map<ClientCallId, number>();
const inboundInviteTimers = new Map<ClientCallId, ReturnType<typeof setTimeout>>();
const pendingServerTerminal = new Map<string, ReturnType<typeof setTimeout>>();
const recentServerTerminals = new Map<string, number>();
let lastForcedReconnectAt = 0;
let lastCallEndedAt = 0;
// One-time wiring guard for the owner publish/command listeners.
let ownerWired = false;
let leadershipTeardown: (() => void) | null = null;

function buildLiveSnapshot(): phoneState.LiveSnapshot {
  return {
    calls: registry.all().map((e) => e.record),
    status: phoneStatus,
    muted: isMutedState,
    disposition: dispositionRecord,
    stats: lastStats,
    lastError: lastCallError,
    notice: lastNotice,
  };
}

function setCallError(clientCallId: ClientCallId, message: string): void {
  lastCallError = { clientCallId, message };
  publishSnapshot();
}

function clearCallError(): void {
  if (!lastCallError) return;
  lastCallError = null;
  publishSnapshot();
}

/** Melding op de pil (buiten een gesprek); verdwijnt vanzelf na 20 s. */
function setNotice(message: string | null): void {
  lastNotice = message;
  if (noticeTimer) {
    clearTimeout(noticeTimer);
    noticeTimer = null;
  }
  if (message) {
    noticeTimer = setTimeout(() => {
      noticeTimer = null;
      lastNotice = null;
      publishSnapshot();
    }, 20_000);
  }
  publishSnapshot();
}

/**
 * Leesbare foutbeschrijving. De SDK gooit soms een kaal object zonder
 * `message`; `String(error)` werd dan letterlijk "[object Object]" in
 * telephony_events (7x bij Ilse in 14 dagen, oorzaak onleesbaar).
 */
function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>;
    const msg = o.message ?? o.error ?? o.reason;
    if (typeof msg === 'string') return msg;
    try {
      return JSON.stringify(err).slice(0, 500);
    } catch {
      /* circulair: val terug op String() */
    }
  }
  return String(err);
}

function publishSnapshot(): void {
  if (!isLeader()) return;
  phoneState.publishOwnerSnapshot(buildLiveSnapshot());
}

function computeStats(sample: Record<string, unknown>): SerializableStats {
  const received = Number(sample.packetsReceived ?? 0);
  const lost = Number(sample.packetsLost ?? 0);
  const denom = received + lost;
  const lossPct = denom > 0 ? (lost / denom) * 100 : 0;
  const quality: SerializableStats['quality'] =
    lossPct < 2 ? 'good' : lossPct < 5 ? 'fair' : 'poor';
  return {
    packetLossPct: lossPct,
    jitterMs: Number(sample.jitterMs ?? 0),
    rttMs: Number(sample.rttMs ?? 0),
    quality,
  };
}

function dispatchCommand(msg: Extract<BusMessage, { kind: 'command' }>): void {
  switch (msg.cmd) {
    case 'answer':
      answerLocal(msg.payload.clientCallId);
      break;
    case 'reject':
      rejectLocal(msg.payload.clientCallId);
      break;
    case 'hangup':
      hangupLocal(msg.payload.clientCallId);
      break;
    case 'mute-toggle':
      toggleMuteLocal(msg.payload.clientCallId);
      break;
    case 'dtmf':
      sendDTMFLocal(msg.payload.clientCallId, msg.payload.digit);
      break;
    case 'dial':
      // Fouten (o.a. DialBlockedError) zijn hier al zichtbaar gemaakt via
      // setCallError; een unhandled rejection zou alleen console-ruis geven.
      dialLocal(msg.payload.number, msg.payload.brand, msg.payload.customer ?? undefined, msg.payload.context ?? undefined).catch(
        () => {},
      );
      break;
    case 'clear-disposition':
      clearDispositionLocal();
      break;
    // NB: 'set-devices' wordt NIET hier afgehandeld maar in de bus-listener
    // in wireOwner() — die draait op élke tab. Voorheen was het owner-only,
    // waardoor andere volger-tabs een verouderde apparaat-cache hielden en de
    // beltoon daar op het oude/default-apparaat bleef klinken.
  }
}

/** Wire owner-side publishing + command handling once (runs on every tab). */
function wireOwner(): void {
  if (ownerWired) return;
  ownerWired = true;

  // Republish on any registry change (publish is itself gated to the owner).
  registry.subscribe(publishSnapshot);

  // Busy-status volgt de registry (state-gedreven): actief gesprek → busy=true
  // naar de server, gesprek voorbij → busy=false. Gated op isLeader() intern.
  registry.subscribe(syncBusyState);

  // Owner-state bij elk gespreks-einde (SDK- én Realtime-pad lopen door
  // dezelfde finalizeCall in call-lifecycle.ts).
  setOnFinalized(({ record, answered, noSdkCallsLeft, reason, source, endedAt }) => {
    if (answered && record) {
      // Surface the post-call disposition form on every tab — behalve wanneer
      // deze leg wegvalt door een eigen conference-upgrade (wachtstand/warm):
      // het gesprek loopt dan gewoon door via de terugbel-leg en een wrap-up
      // middenin voelt als "verbroken" (belproef 07-14).
      if (!hasPendingRedialFor(record.callId)) {
        dispositionRecord = record;
      }
    }
    lastStats = null;
    // Mute is een SDK-per-call toestand; een nieuw gesprek start altijd
    // ongemute. Zonder reset toont de UI na een gemute vorig gesprek de
    // omgekeerde stand en dempt de eerste "unmute"-klik juist de mic.
    if (noSdkCallsLeft) setMutedState(false);
    clearCallError();
    publishSnapshot();
    watchRegistrationAfterEnd(record, reason, source, endedAt);
  });

  // Mirror live call quality into the snapshot so followers render it too.
  subscribeEvents((e) => {
    if (e.type !== 'quality_sample' || !isLeader()) return;
    const sample = (e.metadata as { sample?: Record<string, unknown> } | undefined)?.sample;
    if (!sample) return;
    lastStats = computeStats(sample);
    publishSnapshot();
  });

  // Execute commands proxied from follower tabs.
  phoneBus.onMessage((msg) => {
    if (msg.kind !== 'command') return;
    if (msg.cmd === 'set-devices') {
      // Apparaatvoorkeuren gelden op ÁLLE tabs: elke tab kan (via de toon-
      // lock) de beltoon spelen, dus elke cache moet vers zijn. De afzender
      // heeft al naar Supabase geschreven; BroadcastChannel echoot niet naar
      // de afzender zelf.
      applyCachePatch(msg.payload);
      if (isLeader()) applyDeviceSettingsLocal(msg.payload);
      return;
    }
    if (!isLeader()) return;
    dispatchCommand(msg);
  });
}

// ─── Busy-status → server (ring group-suppressie) ──────────────────
//
// De owner-tab rapporteert of deze agent in gesprek is via
// PUT /api/telephony/presence { busy }. De routering slaat agents met een
// verse busy_since over (telephony-routing.ts), zodat een tweede merk niet
// eens meer gedialed wordt. Een heartbeat elke ~2 min houdt de vlag vers:
// server-side telt busy alleen als busy_since < 5 min oud is, dus een
// gecrashte tab heelt vanzelf. Falen is niet erg — het SDK-pad weigert een
// invite tijdens een actief gesprek sowieso (instant-vangnet).

const BUSY_HEARTBEAT_MS = 2 * 60_000;
let lastReportedBusy = false;
let busyHeartbeatTimer: ReturnType<typeof setInterval> | null = null;

async function pushBusyState(busy: boolean): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('not authenticated');
  const res = await fetch('/api/telephony/presence', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ busy }),
  });
  if (!res.ok) throw new Error(`busy sync failed: ${res.status}`);
}

function syncBusyState(): void {
  if (!isLeader()) return;
  // Lijn bezet i.p.v. alleen actief gesprek: ook tijdens outbound 'dialing'
  // hoort deze agent uit de ring group, anders dialt de routering een inbound
  // dwars door het uitbellen heen.
  const busy = hasLiveCall();
  if (busy !== lastReportedBusy) {
    lastReportedBusy = busy;
    pushBusyState(busy).catch((err) => {
      // Terugzetten zodat de volgende registry-wijziging het opnieuw probeert;
      // de heartbeat (busy) en de server-side 5-min-versheid (idle) dekken de
      // tussentijd af.
      lastReportedBusy = !busy;
      console.warn('[telephony/client] busy sync failed', err);
      emit({
        type: 'busy_sync_failed',
        source: 'client',
        metadata: { busy, message: err instanceof Error ? err.message : String(err) },
      });
    });
  }
  if (busy && !busyHeartbeatTimer) {
    busyHeartbeatTimer = setInterval(() => {
      pushBusyState(true).catch(() => {
        /* volgende heartbeat probeert opnieuw */
      });
    }, BUSY_HEARTBEAT_MS);
  } else if (!busy && busyHeartbeatTimer) {
    clearInterval(busyHeartbeatTimer);
    busyHeartbeatTimer = null;
  }
}

function resetBusyState(): void {
  if (busyHeartbeatTimer) {
    clearInterval(busyHeartbeatTimer);
    busyHeartbeatTimer = null;
  }
  if (lastReportedBusy) {
    lastReportedBusy = false;
    pushBusyState(false).catch(() => {
      /* server-side versheid (5 min) heelt dit vanzelf */
    });
  }
}

// ─── JWT auth ──────────────────────────────────────────────────────

async function fetchLoginToken(): Promise<{ loginToken: string; expiresAt: Date }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  const res = await fetch('/api/telephony/token', {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Token endpoint failed: ${res.status} ${body}`);
  }
  const { loginToken, expiresAt } = (await res.json()) as {
    loginToken: string;
    expiresAt: string;
  };
  return { loginToken, expiresAt: new Date(expiresAt) };
}

function scheduleRefresh(expiresAt: Date): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  // Refresh op ~75% van de resterende TTL (TTL is nu 1u, dus refresh ± 45min);
  // vaste marges van een uur zouden bij een korte TTL elke minuut refreshen.
  const remaining = expiresAt.getTime() - Date.now();
  const delay = Math.max(60_000, Math.floor(remaining * 0.75));
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void refreshToken();
  }, delay);
}

async function refreshToken(): Promise<void> {
  if (!sharedClient || !isLeader()) {
    // Niet stil doven: zonder herplanning verloopt het token onherroepelijk
    // en blijft een later herstelde leader-tab als zombie geregistreerd
    // (incident 2026-07-13). teardownConnection ruimt deze timer op bij
    // resignation/dispose, dus dit lekt niet.
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refreshToken();
    }, 60_000);
    return;
  }
  // Een refresh is een volwaardige disconnect/connect met vers token — de
  // oude options.login_token-mutatie deed niets voor de al-geauthenticeerde
  // socket. Nooit midden in een gesprek: dan even uitstellen.
  if (hasActiveCall()) {
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refreshToken();
    }, 5 * 60 * 1000);
    return;
  }
  try {
    await reconnect('token_refresh');
    emit({ type: 'token_refreshed', source: 'client' });
  } catch (err) {
    console.warn('[telephony/client] token refresh failed', err);
    emit({
      type: 'token_refresh_failed',
      source: 'client',
      metadata: { message: err instanceof Error ? err.message : String(err) },
    });
    // Retry in 5 minutes.
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refreshToken();
    }, 5 * 60 * 1000);
  }
}

// ─── Reconnect (owner-only) ────────────────────────────────────────
//
// De socket kan wegvallen door slaap, netwerkwissel of een verlopen token.
// Elke trigger (socket.close, auth-getinte error, online-event, wake-detectie)
// loopt door hetzelfde pad: verse token + volwaardige disconnect/connect met
// exponentiële backoff (1s → 30s, jitter).

function scheduleReconnect(reason: string): void {
  if (!isLeader() || !userId) return;
  if (reconnectTimer || reconnecting) return;
  const base = Math.min(30_000, 1000 * 2 ** reconnectAttempt);
  const delay = Math.round(base * (0.75 + Math.random() * 0.5));
  reconnectAttempt++;
  emit({
    type: 'client_reconnecting',
    source: 'client',
    metadata: { reason, attempt: reconnectAttempt, delayMs: delay },
  });
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    void reconnect(reason).catch(() => scheduleReconnect('retry'));
  }, delay);
}

async function reconnect(reason: string): Promise<void> {
  if (reconnecting || !isLeader() || !userId) return;
  reconnecting = true;
  try {
    const client = sharedClient;
    sharedClient = null; // vóór disconnect: socket.close mag geen nieuwe reconnect plannen
    connectPromise = null;
    if (client) {
      try {
        await client.disconnect();
      } catch {
        /* ignore */
      }
    }
    await connect(); // haalt zelf een vers token + herplant de refresh-timer
    reconnectAttempt = 0;
    console.info(`[telephony/client] reconnected (${reason})`);
  } finally {
    reconnecting = false;
  }
}

/** Directe gezondheidscheck (online/wake/visible): dood → meteen reconnecten. */
function checkConnectionHealth(reason: string): void {
  if (!isLeader() || !userId) return;
  const c = sharedClient as any;
  const socketHealthy = Boolean(c && c.connected && (!c.state || c.state === 'registered'));
  // Token-verval telt als ongezond, ook al oogt de socket "connected": na
  // slaap/throttling kan de refresh-timer gemist zijn en is de registratie
  // bij Telnyx server-side verlopen terwijl de SDK-state 'registered' blijft
  // (zombie-registratie, incident 2026-07-13). Marge van 5 min zodat we
  // vóór het echte verval al vernieuwen.
  const tokenValid = tokenExpiresAtMs !== null && Date.now() < tokenExpiresAtMs - 5 * 60_000;
  if (socketHealthy && tokenValid) return;
  if (socketHealthy && hasActiveCall()) {
    // Alleen het token is (bijna) verlopen en er loopt een gesprek: nooit
    // mid-call herverbinden (lopende calls overleven token-verval, alleen
    // nieuwe INVITEs niet). Na het gesprek opnieuw checken.
    if (!healthRetryTimer) {
      healthRetryTimer = setTimeout(() => {
        healthRetryTimer = null;
        checkConnectionHealth('post_call');
      }, 60_000);
    }
    return;
  }
  console.warn(`[telephony/client] connection unhealthy after ${reason} — reconnecting`);
  reconnectAttempt = 0; // gebruikerszichtbare trigger: niet eerst lang backoffen
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  emit({ type: 'client_reconnecting', source: 'client', metadata: { reason, attempt: 0, delayMs: 0 } });
  void reconnect(reason).catch(() => scheduleReconnect('retry'));
}

function startWakeWatch(): void {
  if (wakeTimer) return;
  // Slaap/suspend-detectie: een interval dat >60s klokdrift ziet, heeft
  // geslapen — timers lopen niet door tijdens suspend.
  let lastTick = Date.now();
  wakeTimer = setInterval(() => {
    const now = Date.now();
    const drift = now - lastTick;
    lastTick = now;
    if (drift > 60_000) checkConnectionHealth('wake');
  }, 30_000);
  onlineListener = () => checkConnectionHealth('online');
  window.addEventListener('online', onlineListener);
  // Tab-focus na background-throttling: de drift-check kan dat missen
  // (interval tikt net onder de 60s door), maar bij zichtbaar worden moet
  // een verlopen token direct hersteld worden (zombie-registratie 07-13).
  visibilityListener = () => {
    if (document.visibilityState === 'visible') checkConnectionHealth('visible');
  };
  document.addEventListener('visibilitychange', visibilityListener);
}

function stopWakeWatch(): void {
  if (wakeTimer) {
    clearInterval(wakeTimer);
    wakeTimer = null;
  }
  if (onlineListener) {
    window.removeEventListener('online', onlineListener);
    onlineListener = null;
  }
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
}

// ─── Connect / ensureReady ─────────────────────────────────────────

async function connect(): Promise<void> {
  if (sharedClient) return;
  if (connectPromise) return connectPromise;

  connectPromise = (async () => {
    setStatus('connecting');
    emit({ type: 'client_connecting', source: 'client' });

    const { loginToken, expiresAt } = await fetchLoginToken();
    tokenExpiresAtMs = expiresAt.getTime();
    scheduleRefresh(expiresAt);

    const client = new TelnyxRTC({
      login_token: loginToken,
      debug: import.meta.env.DEV,
      region: RTC_REGION,
    } as any);

    // Hand the SDK a persistent <audio> element. It attaches each call's
    // remote MediaStream on the WebRTC `track` event (propagates to every
    // call via call.options.remoteElement), which reliably fixes one-way
    // audio — the old manual attach raced the track and often missed it.
    (client as any).remoteElement = ensureRemoteAudioElement();

    client.on('telnyx.ready', () => {
      console.info('[telephony/client] telnyx.ready — SDK registered');
      setStatus('registered');
      emit({ type: 'client_registered', source: 'sdk' });
    });

    client.on('telnyx.error', (error: any) => {
      console.error('[telephony/client] telnyx.error', error);
      setStatus('error');
      emit({
        type: 'client_error',
        source: 'sdk',
        metadata: { error: describeError(error) },
      });
      // Auth-getinte fouten (verlopen/ingetrokken token) heelt de SDK niet
      // zelf — vers token + reconnect via ons pad.
      const msg = String(error?.message ?? error ?? '').toLowerCase();
      if (sharedClient === client && /auth|token|credential|401|403/.test(msg)) {
        scheduleReconnect('auth_error');
      }
    });

    client.on('telnyx.socket.close', () => {
      console.warn('[telephony/client] telnyx.socket.close');
      setStatus('disconnected');
      emit({ type: 'client_socket_close', source: 'sdk' });
      // Alleen reconnecten als dit nog de actieve client is: bij bewuste
      // teardown/reconnect is sharedClient al genulld vóór disconnect().
      if (sharedClient === client) scheduleReconnect('socket_close');
    });

    client.on('telnyx.socket.open', () => {
      console.info('[telephony/client] telnyx.socket.open');
    });

    client.on('telnyx.notification', (n: any) => {
      handleNotification(n);
    });

    await client.connect();
    sharedClient = client;

    // Microfoonvoorkeur op de SDK zetten. Zonder dit gebruikt elke call de
    // browser-default mic, ongeacht wat er in de instellingen gekozen is
    // (de testknop gebruikte de keuze wél — vandaar "mic werkt buggy").
    try {
      const prefs = await loadPreferences();
      if (prefs?.microphoneId) {
        await (client as any).setAudioSettings({
          micId: prefs.microphoneId,
          micLabel: prefs.microphoneLabel ?? undefined,
        });
      }
    } catch (err) {
      console.warn('[telephony/client] setAudioSettings at connect failed', err);
    }

    emit({ type: 'client_connected', source: 'sdk' });
  })().finally(() => {
    connectPromise = null;
  });

  return connectPromise;
}

async function ensureReady(): Promise<TelnyxRTC> {
  if (!sharedClient) {
    await connect();
  }
  const client = sharedClient!;
  const c = client as any;
  if (!c.connected || (c.state && c.state !== 'registered')) {
    try {
      await c.connect();
    } catch (err) {
      console.warn('[telephony/client] reconnect attempt failed', err);
    }
  }
  return client;
}

// ─── Notification handler ──────────────────────────────────────────

function isOwnDid(digits: string): boolean {
  return OWN_DIDS_DIGITS.has(digits);
}

// A-leg-vóór-POST-race: de bridge-leg-notificatie kan arriveren voordat
// dialLocal het registry-record heeft geseed (de server doet meerdere DB-
// writes vóór de fetch-respons). registry.update() no-opt dan. Bewaar de
// telnyxCallId hier zodat dialLocal 'm na registry.add alsnog koppelt.
const earlyLegs = new Map<ClientCallId, string>();

// Blind transfer naar déze gebruiker: de Realtime-UPDATE die transfer_state
// zet arriveert (vlak) vóór de SIP-invite van de doorverbind-dial. Er komt
// geen Realtime-INSERT (de calls-rij bestaat al), dus parkeren we de context
// hier zodat handleNotification het verse inbound-record kan verrijken met
// callId/conversation/brand/klant. Keyed op klant-digits; TTL 90s zodat een
// nooit-gearriveerde invite geen stale entry achterlaat.
interface PendingTransfer {
  callId: string;
  conversationId: string | null;
  brand: Brand;
  customer: CallerInfo | null;
  /** Weergavenaam van de doorverbindende collega (transfer_state.initiatedByName). */
  transferFrom: string | null;
  mode: 'blind' | 'warm';
  /** Klantnummer (calls.from_number) — bij warm toont de UI hiermee de klant,
   *  want de consult-invite komt binnen vanaf ons eigen DID. */
  customerNumber: string;
  parkedAt: number;
}
const pendingTransfers = new Map<string, PendingTransfer>();
const PENDING_TRANSFER_TTL_MS = 90_000;

function parkPendingTransfer(remoteNumber: string, entry: Omit<PendingTransfer, 'parkedAt'>): void {
  const digits = remoteNumber.replace(/[^\d]/g, '');
  if (!digits) return;
  pendingTransfers.set(digits, { ...entry, parkedAt: Date.now() });
}

function consumePendingTransfer(remoteNumber: string): PendingTransfer | null {
  const digits = remoteNumber.replace(/[^\d]/g, '');
  const hit = pendingTransfers.get(digits);
  if (!hit) return null;
  pendingTransfers.delete(digits);
  if (Date.now() - hit.parkedAt > PENDING_TRANSFER_TTL_MS) return null;
  return hit;
}

/** Warm-consult-fallback: de consult-invite komt van ons eigen DID, dus de
 *  klantnummer-sleutel matcht nooit. Eén warm-entry tegelijk is realistisch
 *  (consult per doel-gebruiker); pak de meest recent geparkeerde. */
function consumePendingWarmTransfer(): PendingTransfer | null {
  let bestKey: string | null = null;
  let best: PendingTransfer | null = null;
  for (const [key, entry] of pendingTransfers) {
    if (entry.mode !== 'warm') continue;
    if (Date.now() - entry.parkedAt > PENDING_TRANSFER_TTL_MS) continue;
    if (!best || entry.parkedAt > best.parkedAt) {
      best = entry;
      bestKey = key;
    }
  }
  if (bestKey) pendingTransfers.delete(bestKey);
  return best;
}

// Terugbel-leg van een conference-upgrade (wachtstand/warm doorverbinden) die
// DEZE gebruiker zelf initieerde: de klant-parent verhuist naar een
// conference, haar eigen leg valt weg en de server belt haar direct terug
// (invite van ons eigen DID). Handmatig opnemen bleek te traag én contextloos
// (belproef 07-14: opnemen op 24s bij een 25s-participant-timeout → "gesprek
// leek verbroken") — daarom wordt deze leg auto-beantwoord en verrijkt met de
// bestaande callId/klantcontext. Geparkeerd op de Realtime-UPDATE die
// supervise_conf op 'call-<id>' zet (de claim, seconden vóór de invite).
// Supervise ('supervise-'-prefix) houdt bewust het handmatige gedrag.
interface PendingRedial {
  callId: string;
  conversationId: string | null;
  brand: Brand;
  /** Klantnummer (calls.from_number) — zo toont de UI de klant, niet ons DID. */
  remoteNumber: string;
  parkedAt: number;
}
let pendingRedial: PendingRedial | null = null;
const PENDING_REDIAL_TTL_MS = 90_000;

function consumePendingRedial(remoteDigits: string): PendingRedial | null {
  const hit = pendingRedial;
  if (!hit) return null;
  if (!isOwnDid(remoteDigits)) return null;
  pendingRedial = null;
  if (Date.now() - hit.parkedAt > PENDING_REDIAL_TTL_MS) return null;
  return hit;
}

/** Peek (niet consumeren): onderdrukt de wrap-up van de wegvallende oude leg. */
function hasPendingRedialFor(callId: string | null): boolean {
  return Boolean(
    pendingRedial &&
    callId &&
    pendingRedial.callId === callId &&
    Date.now() - pendingRedial.parkedAt <= PENDING_REDIAL_TTL_MS,
  );
}

function pickRemoteStream(call: any): MediaStream | null {
  return (
    call?.remoteStream ??
    call?.peer?.remoteStream ??
    call?.peer?.instance?.remoteStream ??
    null
  );
}

function normaliseDirection(dir: string | undefined): CallDirection {
  return dir === 'inbound' || dir === 'incoming' ? 'inbound' : 'outbound';
}

function handleNotification(n: any): void {
  const c = n?.call ?? {};
  const parts = [
    `type=${n?.type}`,
    `state=${c.state ?? 'n/a'}`,
    `prev=${c.prevState ?? '-'}`,
    `dir=${c.direction ?? '-'}`,
    `cause=${c.cause ?? '-'}`,
    `causeCode=${c.causeCode ?? '-'}`,
    `sipCode=${c.sipCode ?? '-'}`,
    `sipReason=${c.sipReason ?? '-'}`,
    `id=${c.id ? String(c.id).slice(0, 8) : '-'}`,
  ];
  console.info('[telephony/client] NOTIF ' + parts.join(' | '));

  const call = n?.call;
  if (!call) return;
  if (n.type !== 'callUpdate') return;

  const direction = normaliseDirection(call.direction);
  const state = call.state as string;

  // First time we see an inbound ringing call → register it.
  if (state === 'ringing' && direction === 'inbound') {
    const existing = registry.getByTelnyxId(call.id);
    if (existing) return;

    // Server-side outbound bridge: the A-leg carries X-ClientCallId in a
    // custom SIP header (Call Control API propagates these). Match against
    // pendingOutbound map → auto-answer without UI ring. Heuristic
    // fallback: own-DID caller-ID + any pending entry, in case custom
    // headers aren't delivered for some transport reason.
    const customHeaders = (call.options?.customHeaders ?? []) as Array<{
      name: string;
      value: string;
    }>;
    const bridgeClientCallId = customHeaders.find(
      (h) => h.name?.toLowerCase() === 'x-clientcallid',
    )?.value;

    const rawRemoteForMatch =
      call.options?.remoteCallerNumber ?? call.options?.callerIdNumber ?? '';
    const remoteDigitsForMatch = rawRemoteForMatch.replace(/[^\d]/g, '');
    const pendingList = pendingOutbound.all();

    let matchedClientCallId: string | null = null;
    if (bridgeClientCallId && pendingOutbound.all().some((p) => p.clientCallId === bridgeClientCallId)) {
      matchedClientCallId = bridgeClientCallId;
    } else if (
      pendingList.length > 0 &&
      remoteDigitsForMatch &&
      isOwnDid(remoteDigitsForMatch)
    ) {
      matchedClientCallId = [...pendingList].sort(
        (a, b) => a.startedAt - b.startedAt,
      )[0]!.clientCallId;
    }

    console.info('[telephony/client] bridge-match check', {
      remote: rawRemoteForMatch,
      digits: remoteDigitsForMatch,
      ownDid: isOwnDid(remoteDigitsForMatch),
      hdrClientCallId: bridgeClientCallId?.slice(0, 8) ?? null,
      pendingCount: pendingList.length,
      matched: matchedClientCallId?.slice(0, 8) ?? null,
    });

    if (matchedClientCallId) {
      pendingOutbound.consumeByClientCallId(matchedClientCallId);

      // Gebruiker hing al op tijdens het kiezen (record in ending/ended, of
      // server-cancel onderweg): deze verse bridge-leg NIET auto-beantwoorden
      // — dat zou een spookgesprek openen. Leg direct weer ophangen.
      const matchedState = registry.get(matchedClientCallId)?.record.state;
      if (matchedState === 'ending' || matchedState === 'ended') {
        console.info(
          '[telephony/client] bridge-leg voor al beëindigd gesprek — ophangen i.p.v. auto-answer',
          matchedClientCallId,
        );
        try {
          call.hangup();
        } catch {
          /* ignore */
        }
        return;
      }

      console.info(
        '[telephony/client] bridge-leg detected — auto-answering',
        matchedClientCallId,
      );
      sdkCalls.set(matchedClientCallId, call);
      if (registry.get(matchedClientCallId)) {
        registry.update(matchedClientCallId, { telnyxCallId: call.id });
      } else {
        earlyLegs.set(matchedClientCallId, call.id);
      }

      try {
        call.answer();
      } catch (err) {
        console.error('[telephony/client] bridge auto-answer failed', err);
      }
      bridgeAnsweredAt.set(matchedClientCallId, Date.now());
      // Bewust géén registry.send(ANSWERED): het record staat in 'dialing' en
      // die state heeft geen ANSWERED-transitie (credential-leg-guard). Dit is
      // ook niet "beantwoord" — alleen de browser-poot staat; de klant rinkelt
      // nog. Het echte answer-signaal komt via Realtime (MEDIA_FLOWING).
      emit({
        type: 'bridge_leg_answered',
        source: 'client',
        clientCallId: matchedClientCallId,
        metadata: {
          bridge: true,
          telnyxCallId: call.id,
          matchMode: bridgeClientCallId ? 'header' : 'heuristic',
        },
      });
      return;
    }

    // Terugbel-leg van een eigen conference-upgrade (wachtstand/warm):
    // auto-beantwoorden en verrijken met de bestaande callId/klantcontext,
    // zodat de wacht-band + Hervat-knop gewoon blijven werken. Vóór de
    // busy-suppressie: de oude leg kan in de race nog als actief geregistreerd
    // staan en deze invite mag nooit geweigerd worden.
    {
      const redial = consumePendingRedial(remoteDigitsForMatch);
      if (redial) {
        console.info('[telephony/client] conference-terugbel-leg — auto-answer', redial.callId);
        const clientCallId = crypto.randomUUID();
        const record: CallRecord = {
          clientCallId,
          callId: redial.callId,
          telnyxCallId: call.id,
          conversationId: redial.conversationId,
          direction: 'inbound',
          state: 'idle',
          brand: redial.brand,
          remoteNumber: redial.remoteNumber,
          startedAt: new Date(),
          answeredAt: null,
          endedAt: null,
          customer: null,
          recoveredFromCallId: null,
          holdSince: null,
          transferState: null,
        };
        sdkCalls.set(clientCallId, call);
        registry.add(record);
        registry.send(clientCallId, { type: 'RING_RECEIVED' });
        try {
          call.answer();
        } catch (err) {
          console.error('[telephony/client] redial auto-answer faalde', err);
          setCallError(clientCallId, 'Terugkeren naar het gesprek mislukte. Neem het belletje handmatig aan.');
          emit({ type: 'answer_failed', source: 'client', clientCallId, metadata: { reason: 'redial_auto_answer_failed' } });
          return;
        }
        registry.send(clientCallId, { type: 'ANSWERED' });
        emit({
          type: 'redial_auto_answered',
          source: 'client',
          clientCallId,
          callId: redial.callId,
          metadata: { telnyxCallId: call.id },
        });
        void stampAgentLeg(clientCallId, call);
        return;
      }
    }

    // The SDK exposes BOTH ends of the call:
    //   callerNumber        = local side (our WebRTC SIP URI, e.g. "gencred...")
    //   remoteCallerNumber  = the actual caller (+31...)
    // Use remoteCallerNumber first; fall back to name/email only if absent.
    const rawRemote =
      call.options?.remoteCallerNumber ??
      call.options?.remoteCallerName ??
      call.options?.callerIdNumber ??
      '';
    // Normalize to E.164 where possible: Telnyx emits "31652823760" (no +).
    const remoteNumber = rawRemote && /^\d+$/.test(rawRemote) && !rawRemote.startsWith('+')
      ? `+${rawRemote}`
      : rawRemote;

    // Race-condition merge: if Realtime seeded a record before the SDK fired,
    // it lives in the registry with telnyxCallId=null. Attach the SDK call to
    // that entry instead of spawning a duplicate one — otherwise the UI keeps
    // showing the seed record (no sdkCall) and Answer fails.
    //
    // Dit blok staat bewust vóór de busy-suppressie hieronder. De Realtime-seed
    // zet zélf een niet-beëindigd record in de registry, dus hasLiveCall() is
    // waar zodra de seed geland is — de daarop volgende SIP-invite van diezelfde
    // call werd dan als "lijn bezet" opgehangen en de UI bleef rinkelen op een
    // record zonder sdkCall ("Aannemen mislukt: geen actieve verbinding",
    // incident 2026-08-13: seed → busy_rejected/in_call → answer_failed/
    // no_sdk_call, zelfde remoteNumber). Eerst matchen, dan pas weigeren:
    // een invite die bij een geseed record hoort is per definitie niet
    // "een tweede lijn".
    //
    // Phone numbers are normalized to digits-only for comparison because the
    // SDK emits '31652823760' while Supabase stores '+31652823760'.
    const digitsOnly = (n: string) => n.replace(/[^\d]/g, '');
    const remoteDigits = digitsOnly(remoteNumber);
    const seeded = registry.all().find((e) =>
      e.record.direction === 'inbound' &&
      e.record.telnyxCallId === null &&
      // Alleen een nog-rinkelend, onbeantwoord seed-record mag deze invite
      // opvangen: anders zou een invite tijdens een lopend (of net beëindigd)
      // gesprek de busy-suppressie omzeilen.
      e.record.answeredAt === null &&
      e.record.state !== 'ended' &&
      e.record.state !== 'ending' &&
      (digitsOnly(e.record.remoteNumber) === remoteDigits || e.record.remoteNumber === '')
    );
    if (seeded) {
      clearInboundInviteWatch(seeded.record.clientCallId);
      sdkCalls.set(seeded.record.clientCallId, call);
      registry.update(seeded.record.clientCallId, {
        telnyxCallId: call.id,
        remoteNumber: remoteNumber || seeded.record.remoteNumber,
      });
      emit({
        type: 'ringing',
        source: 'sdk',
        clientCallId: seeded.record.clientCallId,
        metadata: { telnyxCallId: call.id, remoteNumber, merged: true },
      });
      return;
    }

    // Busy-suppressie: is de lijn al bezet (actief gesprek, of een uitgaand
    // gesprek dat nog bij de klant rinkelt), dan wordt een
    // nieuwe inbound invite direct geweigerd — geen UI-ring, geen beltoon,
    // geen registry-record. De ring group (multi-<Sip>) laat andere agents
    // gewoon doorrinkelen; weigeren alle legs, dan valt de beller meteen
    // door naar de overflow-actie (BizziPhone/voicemail) i.p.v. de volle
    // ring-timeout af te wachten. Zonder dit sloopte het einde van de
    // tweede (onbeantwoorde) leg de audio van het lopende gesprek
    // (incident 2026-07-06). Server-side vangt de routering dit ook af
    // (busy_since-filter), maar die cache is tot 60s stale — dit is het
    // instant-vangnet.
    // Afwezig telt óók: de routering dialt een dnd-agent niet, maar binnen
    // het 60s-config-cachevenster na het omzetten kan er nog een invite
    // arriveren — die hoort dan stil geweigerd te worden.
    const isAway = getPresenceState().presence === 'dnd';
    if (hasLiveCall() || isAway) {
      console.info(
        `[telephony/client] inbound invite geweigerd — ${isAway ? 'agent afwezig' : 'lijn bezet'}`,
        call.id,
      );
      try {
        call.hangup();
      } catch {
        /* ignore */
      }
      emit({
        type: 'busy_rejected',
        source: 'client',
        metadata: {
          reason: isAway ? 'away' : 'in_call',
          telnyxCallId: call.id,
          remoteNumber:
            call.options?.remoteCallerNumber ?? call.options?.callerIdNumber ?? null,
        },
      });
      return;
    }

    const clientCallId = crypto.randomUUID();
    const recoveredCallId: string | null = call.options?.recoveredCallId ?? null;

    // Blind transfer naar déze gebruiker: de calls-rij bestaat al (geen
    // Realtime-INSERT-seed), dus zonder deze verrijking bleef record.callId
    // null en kon de answer-PATCH answered_by nooit flippen. De pending-
    // transfer-entry (gezet door de Realtime-UPDATE op transfer_state
    // hieronder) draagt callId/conversation/brand/klant mee.
    const pendingTransfer =
      consumePendingTransfer(remoteNumber) ??
      // Warm consult: de invite komt van ons eigen DID, niet van de klant —
      // de klantnummer-sleutel matcht dan nooit. Alleen bij een eigen-DID-
      // invite de warm-entry consumeren (een gewone klant-call mag nooit per
      // ongeluk transfer-context krijgen).
      (isOwnDid(remoteDigits) ? consumePendingWarmTransfer() : null);

    const record: CallRecord = {
      clientCallId,
      callId: pendingTransfer?.callId ?? null,
      telnyxCallId: call.id,
      conversationId: pendingTransfer?.conversationId ?? null,
      direction: 'inbound',
      state: 'idle',
      brand: pendingTransfer?.brand ?? 'CINCO',
      // Warm consult: toon de klant (context-panel + naam), niet ons DID.
      remoteNumber:
        pendingTransfer?.mode === 'warm' && pendingTransfer.customerNumber
          ? pendingTransfer.customerNumber
          : remoteNumber,
      startedAt: new Date(),
      answeredAt: null,
      endedAt: null,
      customer: pendingTransfer?.customer ?? null,
      recoveredFromCallId: recoveredCallId,
      holdSince: null,
      transferState: null,
      incomingTransfer: pendingTransfer
        ? { from: pendingTransfer.transferFrom, mode: pendingTransfer.mode }
        : null,
    };

    sdkCalls.set(clientCallId, call);
    registry.add(record);
    registry.send(clientCallId, { type: 'RING_RECEIVED' });

    if (recoveredCallId) {
      registry.mergeRecovered(clientCallId, recoveredCallId);
      emit({
        type: 'recovered',
        source: 'sdk',
        clientCallId,
        metadata: { fromId: recoveredCallId },
      });
    }

    // Beltoon komt uit de tone-driver (state-gedreven, elke tab); alleen de
    // desktop-notificatie blijft owner-only om duplicaten te voorkomen.
    if (isLeader()) {
      showIncoming({
        clientCallId,
        callerName: record.customer?.name || record.remoteNumber || 'Onbekend nummer',
        brand: record.brand,
        transfer: record.incomingTransfer ?? null,
        onClick: () => {
          /* UI picks up focus via registry subscription; no-op. */
        },
      });
    }

    emit({
      type: 'ringing',
      source: 'sdk',
      clientCallId,
      metadata: { telnyxCallId: call.id, remoteNumber: record.remoteNumber },
    });
    return;
  }

  // Outbound ringing notifications arrive after dial(); the entry is already
  // present in the registry (added by dial()) — just log.
  if (state === 'ringing' && direction === 'outbound') {
    const clientCallId = registry.getByTelnyxId(call.id);
    if (!clientCallId) return;
    emit({
      type: 'sdp_sent',
      source: 'sdk',
      clientCallId,
      metadata: { telnyxCallId: call.id },
    });
    return;
  }

  // Later state updates for both directions.
  const clientCallId = registry.getByTelnyxId(call.id);
  if (!clientCallId) return;

  if (state === 'active') {
    const entry = registry.get(clientCallId);

    // Outbound bridge: 'active' betekent hier alleen dat de browser-poot
    // (A-leg, auto-beantwoord) staat — de klant rinkelt op dat moment vaak
    // nog. De echte answer komt via Supabase Realtime zodra de webhook de
    // B-leg gebridged heeft (calls.status → 'active'). Audio-pad wél alvast
    // koppelen zodat het gesprek direct hoorbaar is zodra de bridge staat;
    // de lokale ringback blijft spelen tot de Realtime-flip.
    if (entry?.record.direction === 'outbound') {
      attachAudioFor(clientCallId, call);
      return;
    }

    if (entry && entry.record.state !== 'active') {
      registry.send(clientCallId, { type: 'MEDIA_FLOWING' });
    }
    attachAudioFor(clientCallId, call);
    startSampling(clientCallId, call);
    emit({ type: 'answered', source: 'sdk', clientCallId });
    // Server-side Telnyx start recording op `call.answered` event
    // (zie api/inbox/telnyx-webhook.ts handleCallAnswered). Dit event
    // markeert de UI zichtbaarheid — niet de server-start.
    emit({ type: 'recording_started', source: 'client', clientCallId });
    return;
  }

  if (state === 'hangup' || state === 'destroy') {
    // Alle cleanup (audio, notificatie, sampling, dispositie, mute-reset)
    // loopt door de gedeelde finalizer — zelfde pad als het Realtime-einde.
    finalizeCall(clientCallId, state, 'sdk', {
      cause: c.cause ?? null,
      causeCode: c.causeCode ?? null,
      sipCode: c.sipCode ?? null,
      sipReason: c.sipReason ?? null,
    });
  }
}

function attachAudioFor(_clientCallId: ClientCallId, call: any): void {
  // The SDK attaches the remote stream to the shared remoteElement on its
  // `track` event; here we just (re)apply the persisted speaker sink at call
  // start. Fallback: if the SDK already exposes a stream but hasn't attached
  // it (e.g. track fired before remoteElement was set), attach it manually to
  // the SAME shared element — idempotent, no double audio.
  const el = ensureRemoteAudioElement();
  applyRemoteSpeakerSink();
  if (!el.srcObject) {
    const stream = pickRemoteStream(call);
    if (stream) {
      el.srcObject = stream;
      el.play().catch(() => {
        // Autoplay policy — resolves on next user gesture.
      });
    }
  }
}

function detachAudioFor(_clientCallId: ClientCallId): void {
  clearRemoteAudio();
}

// ─── Supabase Realtime subscription ────────────────────────────────

async function lookupCustomer(number: string): Promise<CallerInfo | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return null;
    const res = await fetch(`/api/customers/search?q=${encodeURIComponent(number)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { results?: Array<{ id: string; name: string; type: 'account' | 'lead' }> };
    const r = data.results?.[0];
    return r ? { id: r.id, name: r.name, type: r.type } : null;
  } catch {
    return null;
  }
}

function subscribeRealtime(): void {
  if (realtimeChannel) return;

  realtimeChannel = supabase
    .channel('calls-realtime')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'calls',
        filter: 'direction=eq.inbound',
      },
      async (payload) => {
        const call = payload.new as Record<string, unknown>;
        const dbCallId = call.id as string;
        const remoteNumber = (call.from_number as string) ?? '';
        const brand = ((call.brand as string) ?? 'CINCO') as Brand;

        // Routering v2 zet routing_stage bij insert. Alleen bij 'ring' wordt de
        // softphone ook echt gebeld; bij 'voicemail'/'forward' (dicht, of lege
        // ring group) komt er nooit een SIP-invite en dus ook nooit een cancel —
        // een geseede ring zou dan eindeloos doorrinkelen terwijl de klant
        // inspreekt. Legacy-inserts hebben routing_stage=null en dialen wél.
        const routingStage = (call.routing_stage as string | null) ?? 'ring';
        if (routingStage !== 'ring') return;

        // Busy-suppressie (zelfde regel als het SDK-pad): bij een bezette lijn
        // (actief of nog uitbellend) geen ring-record seeden — de SDK-invite
        // wordt geweigerd, dus een seed zou als spook-"rinkelt" blijven hangen
        // tot de terminal-flip.
        if (hasLiveCall()) return;

        // Afwezig-suppressie: de routering dialt een dnd-agent niet (geen
        // SIP-invite), maar deze Realtime-seed is presence-blind en liet de
        // tab van een afwezige agent tóch rinkelen — zonder cancel, tot de
        // terminal-flip (gezien bij Albert, 2026-07-06). Afwezig = stil.
        if (getPresenceState().presence === 'dnd') return;

        // Ring-target-suppressie: ring_enabled=false betekent dat de routering
        // deze gebruiker nooit dialt — er komt dus nooit een SIP-invite en
        // opnemen faalt gegarandeerd met no_sdk_call (incident 2026-07-07:
        // spook-ring op een niet-target-account, "Aannemen mislukt"). Alleen
        // onderdrukken als de presence-fetch al geland is; is die er nog niet,
        // dan laat een echte invite (SDK-pad) de call alsnog gewoon rinkelen.
        {
          const p = getPresenceState();
          if (p.loaded && !p.ringEnabled) return;
        }

        // Find an existing inbound ringing record without a DB callId yet.
        // Numbers are normalized to digits-only because the SDK emits
        // '31652823760' while Supabase stores '+31652823760'.
        const digitsOnly = (n: string) => n.replace(/[^\d]/g, '');
        const remoteDigits = digitsOnly(remoteNumber);
        const findExisting = () =>
          registry.all().find((e) =>
            e.record.direction === 'inbound' &&
            (e.record.callId === dbCallId ||
              (e.record.callId === null &&
                (digitsOnly(e.record.remoteNumber) === remoteDigits ||
                  e.record.remoteNumber === ''))),
          );

        const customer = await lookupCustomer(remoteNumber);

        // Her-check ná de await (check-then-act-race): bij routering v2 wordt
        // de calls-rij geïnsert vóór de SIP-invite, dus Realtime verslaat de
        // SDK. Tijdens de klant-lookup (~1-2s) komt de SDK-invite binnen en
        // maakt zijn eigen record; zonder her-check seedden we dan alsnog een
        // spook-duplicaat dat in 'ringing' bleef hangen en ná het gesprek
        // opnieuw ging rinkelen (telemetrie 2026-07-03: bij élke call).
        const pending = findExisting();

        if (pending) {
          registry.update(pending.record.clientCallId, {
            callId: dbCallId,
            conversationId: (call.conversation_id as string) ?? null,
            brand,
            remoteNumber: remoteNumber || pending.record.remoteNumber,
            customer: customer ?? pending.record.customer,
          });
        } else {
          // Her-check van de busy-suppressie ná de await (dezelfde check-then-
          // act-race als hierboven): begon er tijdens de klant-lookup een ander
          // gesprek, dan is de lijn nu bezet, wordt de SIP-invite van déze call
          // geweigerd en zou de seed als spook-"rinkelt" blijven staan — met
          // "Aannemen mislukt" als de agent er toch op drukt.
          if (hasLiveCall()) return;

          // Realtime beat the SDK notification — seed a record from DB data so
          // the UI still sees the call. (Rare path; audio still arrives via SDK.)
          const clientCallId = crypto.randomUUID();
          const record: CallRecord = {
            clientCallId,
            callId: dbCallId,
            telnyxCallId: null,
            conversationId: (call.conversation_id as string) ?? null,
            direction: 'inbound',
            state: 'idle',
            brand,
            remoteNumber,
            startedAt: new Date((call.started_at as string) ?? Date.now()),
            answeredAt: null,
            endedAt: null,
            customer,
            recoveredFromCallId: null,
            holdSince: null,
            transferState: null,
          };
          registry.add(record);
          registry.send(clientCallId, { type: 'RING_RECEIVED' });
          startInboundInviteWatch(clientCallId);
          if (isLeader()) {
            showIncoming({
              clientCallId,
              callerName: customer?.name || remoteNumber || 'Onbekend nummer',
              brand,
              onClick: () => {
                /* no-op; focus is enough */
              },
            });
          }
          emit({
            type: 'ringing',
            source: 'realtime',
            clientCallId,
            callId: dbCallId,
          });
        }
      },
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'calls',
      },
      (payload) => {
        const updated = payload.new as Record<string, unknown>;
        const dbCallId = updated.id as string;
        {
          const st = updated.status as string;
          if (
            st === 'completed' ||
            st === 'missed' ||
            st === 'rejected' ||
            st === 'forwarded' ||
            st === 'voicemail' ||
            st === 'failed'
          ) {
            noteServerTerminal(dbCallId);
          }
        }
        const transferState = (updated.transfer_state ?? null) as
          | (TransferStateInfo & { targetUserId?: string | null })
          | null;

        // Terugbel-verwachting parkeren: de hold/warm-claim ('call-<id>' op
        // supervise_conf) van een gesprek dat DEZE gebruiker aannam betekent
        // dat onze eigen leg zo wegvalt en de server ons direct terugbelt —
        // die invite wordt auto-beantwoord (zie pendingRedial hierboven).
        const superviseConf = (updated.supervise_conf as string | null) ?? null;
        if (
          superviseConf?.startsWith('call-') &&
          (updated.answered_by as string | null) === userId
        ) {
          pendingRedial = {
            callId: dbCallId,
            conversationId: (updated.conversation_id as string) ?? null,
            brand: ((updated.brand as string) ?? 'CINCO') as Brand,
            remoteNumber: (updated.from_number as string) ?? '',
            parkedAt: Date.now(),
          };
        }

        const clientCallId = registry.getByCallId(dbCallId);
        if (!clientCallId) {
          // Blind/warm transfer met déze gebruiker als doel: context parkeren
          // voor de SIP-invite die zo binnenkomt (zie pendingTransfers).
          if (
            transferState?.targetUserId === userId &&
            (updated.status as string) === 'active'
          ) {
            parkPendingTransfer((updated.from_number as string) ?? '', {
              callId: dbCallId,
              conversationId: (updated.conversation_id as string) ?? null,
              brand: ((updated.brand as string) ?? 'CINCO') as Brand,
              customer: null,
              transferFrom: transferState.initiatedByName ?? null,
              mode: transferState.mode ?? 'blind',
              customerNumber: (updated.from_number as string) ?? '',
            });
          }
          return;
        }

        // Server-gedreven gespreksstaat (wachtstand + transfer) spiegelen —
        // alle tabs tonen dezelfde staat via het snapshot.
        {
          const entry = registry.get(clientCallId);
          const holdSince = updated.hold_since
            ? new Date(updated.hold_since as string)
            : null;
          if (
            entry &&
            ((entry.record.holdSince?.getTime() ?? null) !== (holdSince?.getTime() ?? null) ||
              JSON.stringify(entry.record.transferState) !== JSON.stringify(transferState))
          ) {
            registry.update(clientCallId, { holdSince, transferState });
          }
        }

        const status = updated.status as string;
        if (status === 'active') {
          const entry = registry.get(clientCallId);
          const wasActive = entry?.record.state === 'active';
          // Gedeeld inkomend gesprek dat een cóllega opnam: de ringing→active-
          // flip op de gedeelde calls-rij wordt naar élke meebellende tab
          // gebroadcast. Zonder deze guard zette het generieke MEDIA_FLOWING
          // hieronder answeredAt óók bij de niet-opnemers (machine: ringing +
          // MEDIA_FLOWING → markAnswered) → iedereen kreeg bij het einde het
          // "Gesprek afgerond"-wrap-up-scherm. answered_by draagt de opnemer
          // (call.ts PATCH stempelt 'm mee met de flip). Is dat niet deze
          // gebruiker en heeft deze tab zelf niet aangenomen, dan is de race
          // verloren: ring-popup dicht zónder wrap-up (answeredAt blijft null).
          const answeredBy = (updated.answered_by as string | null) ?? null;
          // Rinkelende transfer-doel-leg: is déze gebruiker het doel van een
          // lopende transfer, dan ís de rinkelende leg de doorverbind-dial —
          // maar de gedeelde rij draagt nog answered_by van de doorverbindende
          // collega + status 'active'. Elke rij-UPDATE tijdens het transfer-
          // rinkelen doodde de leg hieronder binnen een seconde als
          // "answered_elsewhere" → transfer altijd no-answer (incident
          // 2026-07-30, Ilse→Willem). En de kill alléén overslaan is niet
          // genoeg: MEDIA_FLOWING zou de rinkelende leg dan markAnswered'en
          // (machine: ringing + MEDIA_FLOWING → active). Dus: status-
          // verwerking volledig negeren zolang de transfer-doel-leg rinkelt.
          // Het echte vervolg komt via het SDK-pad: ANSWERED bij opnemen,
          // DESTROYED als Telnyx de invite cancelt (restore/timeout).
          const isTransferTargetLeg = transferState?.targetUserId === userId;
          if (
            isTransferTargetLeg &&
            entry &&
            entry.record.direction === 'inbound' &&
            !entry.record.answeredAt &&
            answeredBy !== userId
          ) {
            return;
          }
          if (
            entry &&
            entry.record.direction === 'inbound' &&
            !entry.record.answeredAt &&
            answeredBy &&
            answeredBy !== userId
          ) {
            finalizeCall(clientCallId, 'answered_elsewhere', 'realtime');
            return;
          }
          registry.send(clientCallId, { type: 'MEDIA_FLOWING' });
          // Outbound: dít is het echte answer-signaal (webhook zet 'active'
          // pas als de B-leg gebridged is). De sampling + events die inbound
          // al via het SDK-pad krijgt; de ringback stopt via de tone-driver.
          if (entry && !wasActive && entry.record.direction === 'outbound') {
            const sdkCall = sdkCalls.get(clientCallId);
            if (sdkCall) {
              attachAudioFor(clientCallId, sdkCall);
              startSampling(clientCallId, sdkCall);
            }
            emit({ type: 'answered', source: 'realtime', clientCallId });
            emit({ type: 'recording_started', source: 'client', clientCallId });
          }
        } else if (
          status === 'completed' ||
          status === 'missed' ||
          status === 'rejected' ||
          status === 'forwarded' ||
          status === 'voicemail' ||
          status === 'failed'
        ) {
          // Zelfde finalizer als het SDK-pad: vóór deze unificatie sloeg het
          // Realtime-einde sampling/audio/dispositie/mute-reset over.
          finalizeCall(clientCallId, status, 'realtime');
        }
      },
    )
    .subscribe((subStatus) => {
      // Kanaal-gezondheid bewaken: een stil weggevallen kanaal betekent geen
      // inbound-seeds en geen answer/terminal-flips meer (ringback over een
      // live gesprek). Bij fouten: afbreken en met backoff opnieuw opbouwen.
      if (subStatus === 'SUBSCRIBED') {
        if (realtimeBackoff > 0) {
          emit({ type: 'realtime_channel_recovered', source: 'client' });
        }
        realtimeBackoff = 0;
        return;
      }
      if (subStatus === 'CHANNEL_ERROR' || subStatus === 'TIMED_OUT' || subStatus === 'CLOSED') {
        // Bewuste teardown (unsubscribeRealtime) nult realtimeChannel vóór
        // removeChannel — de CLOSED die daarop volgt is geen storing.
        if (!realtimeChannel) return;
        console.warn(`[telephony/client] realtime channel ${subStatus} — resubscribe met backoff`);
        emit({ type: 'realtime_channel_down', source: 'client', metadata: { status: subStatus } });
        unsubscribeRealtime();
        if (!isLeader()) return;
        const delay = Math.min(30_000, 1000 * 2 ** realtimeBackoff);
        realtimeBackoff++;
        setTimeout(() => {
          if (isLeader() && !realtimeChannel) subscribeRealtime();
        }, delay);
      }
    });
}

function unsubscribeRealtime(): void {
  if (realtimeChannel) {
    const ch = realtimeChannel;
    realtimeChannel = null; // vóór removeChannel: de CLOSED-callback mag niet her-subscriben
    void supabase.removeChannel(ch);
  }
}

// ─── Public API ────────────────────────────────────────────────────

export async function initTelephony(uid: string): Promise<void> {
  userId = uid;
  setEventUser(uid);

  // Wire the cross-tab mirror + owner publishing/command handling. Both run on
  // every tab; the owner-only work is gated on isLeader() internally.
  phoneState.initPhoneState();
  wireOwner();

  // Eigen presence (afwezig/beschikbaar) vroeg laden: de invite-weigering en
  // de Realtime-seed-gate lezen 'm synchroon uit de store.
  initPresenceStore();

  // Bel-/ringbacktonen zijn state-gedreven en spelen op elke tab (zie
  // tone-driver.ts) — een owner-achtergrondtab zonder user-activation kan
  // het geluid dan niet meer gijzelen.
  initToneDriver();

  // Audio-watchdog: bewaakt tijdens actieve gesprekken dat het gedeelde
  // remote-element speelt (idempotent, owner-gated in de tick zelf).
  startAudioWatchdog();

  // Hydrate device preferences (non-blocking for connect).
  void loadPreferences();

  // Contend for ownership. Only the winning tab connects + subscribes to
  // Realtime; followers mirror it. The browser auto-hands ownership to the
  // next tab if the owner closes or crashes (Web Locks).
  leadershipTeardown = acquireLeadership({
    onBecomeLeader: () => {
      subscribeRealtime();
      publishSnapshot();
      startWakeWatch();
      void connect().catch((err) => {
        console.error('[telephony/client] connect failed', err);
        setStatus('error');
        emit({
          type: 'client_error',
          source: 'client',
          metadata: { message: err instanceof Error ? err.message : String(err) },
        });
        scheduleReconnect('initial_connect_failed');
      });
    },
    onResign: () => {
      teardownConnection();
    },
  });

  // Ask the current owner (if any) for an immediate snapshot so this tab's UI
  // is correct before the first change fires.
  phoneState.requestSync();
}

/**
 * Tear down the owner's connection, Realtime subscription and live call state.
 * Runs on resignation (another tab takes over) and on dispose. Idempotent.
 */
function teardownConnection(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (healthRetryTimer) {
    clearTimeout(healthRetryTimer);
    healthRetryTimer = null;
  }
  reconnectAttempt = 0;
  stopWakeWatch();
  resetBusyState();
  unsubscribeRealtime();
  realtimeBackoff = 0;
  pendingOutbound.clear();
  registry.all().forEach((e) => {
    detachAudioFor(e.record.clientCallId);
    registry.remove(e.record.clientCallId);
  });
  resetLifecycle();
  earlyLegs.clear();
  dispositionRecord = null;
  lastStats = null;
  lastCallError = null;
  const client = sharedClient;
  sharedClient = null;
  connectPromise = null;
  if (client) {
    void client.disconnect().catch(() => {
      /* ignore */
    });
  }
  setStatus('disconnected');
  phoneState.resetState();
}

export async function disposeTelephony(): Promise<void> {
  // Releasing leadership fires onResign (→ teardownConnection) if we were the
  // owner; on a follower it just drops us from the lock queue.
  leadershipTeardown?.();
  leadershipTeardown = null;
  if (audioWatchdogTimer) {
    clearInterval(audioWatchdogTimer);
    audioWatchdogTimer = null;
  }
  teardownConnection();
  phoneBus.closeBus();
  userId = null;
  setEventUser(null);
}

// ─── Registratie-bewaker ───────────────────────────────────────────
//
// Beslislogica in registration-watch.ts; hier de timers en de uitvoering.
// Een verdenking leidt tot hoogstens één geforceerde herverbinding per
// minuut, nooit tijdens een lopend gesprek; elke verdenking wordt gelogd
// als registration_suspect (met de genomen actie) zodat het effect meetbaar
// is: na de herverbinding hoort het volgende gesprek gewoon te lopen.

function watchRegistrationAfterEnd(
  record: CallRecord | null,
  reason: string,
  source: TelephonyEventSource,
  endedAt: number,
): void {
  lastCallEndedAt = endedAt;
  if (!record) return;
  clearInboundInviteWatch(record.clientCallId);
  const bridgeAt = bridgeAnsweredAt.get(record.clientCallId) ?? null;
  bridgeAnsweredAt.delete(record.clientCallId);

  const verdict = assessOutboundEnd({
    direction: record.direction,
    stateBeforeEnd: record.state,
    answeredAt: record.answeredAt,
    startedAt: record.startedAt,
    bridgeAnsweredAt: bridgeAt,
    endedAt,
    reason,
    source,
  });
  if (verdict.kind === 'none') return;
  if (verdict.kind === 'suspect') {
    suspectDeadRegistration(verdict.reason, record.clientCallId, {
      elapsedMs: verdict.elapsedMs,
      endReason: reason,
    });
    return;
  }

  // Snelle SDK-val na het opnemen van de A-leg: alleen verdacht als de
  // server-terminal (Realtime 'completed') níet meekomt. Zonder callId valt
  // er niets te correleren: dan geen oordeel.
  const callId = record.callId;
  if (!callId) return;
  const seenAt = recentServerTerminals.get(callId);
  if (seenAt !== undefined && Math.abs(seenAt - endedAt) <= SERVER_TERMINAL_GRACE_MS) return;
  const existing = pendingServerTerminal.get(callId);
  if (existing) clearTimeout(existing);
  pendingServerTerminal.set(
    callId,
    setTimeout(() => {
      pendingServerTerminal.delete(callId);
      suspectDeadRegistration(verdict.reason, record.clientCallId, {
        elapsedMs: verdict.elapsedMs,
        waitedMs: SERVER_TERMINAL_GRACE_MS,
        endReason: reason,
      });
    }, SERVER_TERMINAL_GRACE_MS),
  );
}

/** Realtime meldde een eindstatus op deze calls-rij: Telnyx finaliseerde mee. */
function noteServerTerminal(callId: string): void {
  recentServerTerminals.set(callId, Date.now());
  setTimeout(() => recentServerTerminals.delete(callId), 30_000);
  const t = pendingServerTerminal.get(callId);
  if (t) {
    clearTimeout(t);
    pendingServerTerminal.delete(callId);
  }
}

/** Realtime-seed op 'ring'-stage: binnen INVITE_WAIT_MS hoort de SDK-invite er te zijn. */
function startInboundInviteWatch(clientCallId: ClientCallId): void {
  clearInboundInviteWatch(clientCallId);
  inboundInviteTimers.set(
    clientCallId,
    setTimeout(() => {
      inboundInviteTimers.delete(clientCallId);
      if (sdkCalls.has(clientCallId)) return;
      const entry = registry.get(clientCallId);
      if (entry?.record.telnyxCallId) return;
      suspectDeadRegistration('invite_not_delivered', clientCallId, {
        waitedMs: INVITE_WAIT_MS,
        stillRinging: Boolean(entry && entry.record.state !== 'ended'),
      });
    }, INVITE_WAIT_MS),
  );
}

function clearInboundInviteWatch(clientCallId: ClientCallId): void {
  const t = inboundInviteTimers.get(clientCallId);
  if (t) {
    clearTimeout(t);
    inboundInviteTimers.delete(clientCallId);
  }
}

function suspectDeadRegistration(
  reason: SuspectReason,
  clientCallId: ClientCallId | undefined,
  detail: Record<string, unknown>,
): void {
  if (!isLeader() || !userId) return;
  const now = Date.now();
  const action = decideSuspectAction({
    now,
    hasActiveCall: hasActiveCall(),
    reconnectBusy: reconnecting || reconnectTimer !== null,
    lastForcedReconnectAt,
    lastCallEndedAt,
    reason,
  });
  emit({
    type: 'registration_suspect',
    source: 'client',
    clientCallId,
    metadata: { reason, action, sdkStatus: phoneStatus, ...detail },
  });
  if (action !== 'reconnect') return;
  lastForcedReconnectAt = now;
  console.warn(`[telephony/client] registratie verdacht (${reason}) — geforceerde herverbinding`);
  const message = noticeFor(reason);
  // Rinkelt het seed-record nog, dan hoort de uitleg op die kaart; de pil
  // toont dezelfde melding voor wie het gesprek al weg zag vallen.
  if (clientCallId && registry.get(clientCallId)) setCallError(clientCallId, message);
  setNotice(message);
  reconnectAttempt = 0;
  void reconnect(`suspect_${reason}`).catch(() => scheduleReconnect('retry'));
}

function hasActiveCall(): boolean {
  return registry
    .all()
    .some((e) => e.record.state === 'active' || e.record.answeredAt !== null);
}

// "Lijn bezet": elk niet-beëindigd gesprek telt mee, ook een uitgaand gesprek
// dat nog in 'dialing' staat (klant rinkelt nog; answeredAt blijft daar bewust
// null tot de Realtime-flip). hasActiveCall() bleef in dat venster false,
// waardoor een inbound invite tijdens het uitbellen gewoon doorrinkelde en
// aangenomen kon worden naast de lopende uitgaande lijn (bug 2026-07-21).
// Zelfde predicaat als de dial-guard in dialLocal. hasActiveCall() blijft
// bestaan voor plekken waar het om lopende media gaat (audio-watchdog,
// beforeunload, reconnect-uitstel).
function hasLiveCall(exceptClientCallId?: ClientCallId): boolean {
  return registry
    .all()
    .some(
      (e) =>
        e.record.state !== 'ended' &&
        e.record.clientCallId !== exceptClientCallId,
    );
}

// ─── Audio-watchdog ────────────────────────────────────────────────
//
// Tijdens een actief gesprek hoort het gedeelde remote-element te spelen mét
// stream. Zo niet: event (zichtbaar in telephony_events) + zachte autoheal
// via play(). Vangt de klasse "geluid weg, gesprek loopt door" waarvan de
// trigger op 2026-07-07 niet te achterhalen was. Owner-only; 10s-cadans,
// event-dedup 30s.
let audioWatchdogTimer: ReturnType<typeof setInterval> | null = null;
let lastWatchdogEventAt = 0;

function startAudioWatchdog(): void {
  if (audioWatchdogTimer) return;
  audioWatchdogTimer = setInterval(() => {
    if (!isLeader() || !hasActiveCall()) return;
    const health = getRemoteAudioHealth();
    if (health.exists && !health.paused && health.hasStream) return;
    // Heal naar het faalmechanisme: zonder stream valt er niets te play()-en.
    // Alle anomalies t/m 2026-07-28 waren hasStream=false + resumed=false —
    // het track-event van de SDK kwam laat (trage ICE) of miste het element,
    // en niets koppelde de stream daarna alsnog aan. Hier dus eerst de remote
    // stream van de actieve SDK-call opnieuw aankoppelen, dan pas resume.
    let reattached = false;
    if (!health.hasStream) {
      const active = registry
        .all()
        .find((e) => e.record.state === 'active' || e.record.answeredAt !== null);
      const sdkCall = active ? (sdkCalls.get(active.record.clientCallId) as any) : undefined;
      if (active && sdkCall && pickRemoteStream(sdkCall)) {
        attachAudioFor(active.record.clientCallId, sdkCall);
        reattached = getRemoteAudioHealth().hasStream;
      }
    }
    void tryResumeRemoteAudio().then((resumed) => {
      const now = Date.now();
      if (now - lastWatchdogEventAt < 30_000) return;
      lastWatchdogEventAt = now;
      emit({
        type: 'audio_watchdog_anomaly',
        source: 'client',
        metadata: {
          ...health,
          resumed,
          reattached,
          path: typeof window !== 'undefined' ? window.location.pathname : null,
        },
      });
    });
  }, 10_000);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    // Warn before closing the owner tab mid-call — the live connection + audio
    // live here and would drop. (Another tab still takes over after unload.)
    if (isLeader() && hasActiveCall()) {
      event.preventDefault();
      event.returnValue = '';
      return;
    }
    void disposeTelephony();
  });
}

// ─── Outbound dial ─────────────────────────────────────────────────

function normaliseToE164(phone: string): string {
  let cleaned = phone.replace(/[^\d+]/g, '');
  // Internationaal 00-prefix eerst: '0032...' is België, geen NL '0'-nummer.
  if (cleaned.startsWith('00')) cleaned = `+${cleaned.slice(2)}`;
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('31') && cleaned.length === 11) return '+' + cleaned;
  if (cleaned.startsWith('0') && cleaned.length === 10) return '+31' + cleaned.slice(1);
  // Onherkenbaar formaat: niet blind '+31' voorplakken (verminkt buitenlandse
  // of onvolledige nummers) — warn en geef door; de server valideert verder.
  console.warn('[telephony/client] normaliseToE164: onherkenbaar nummer, ongewijzigd doorgegeven:', phone);
  return cleaned || phone;
}

/** Tweede uitgaande call geweigerd: er loopt al een gesprek. */
export class DialBlockedError extends Error {
  constructor() {
    super('Je bent nog in gesprek. Beëindig het huidige gesprek eerst.');
    this.name = 'DialBlockedError';
  }
}

export async function dial(
  number: string,
  brand: Brand | string,
  customer?: CallerInfo,
  context?: DialContext,
): Promise<ClientCallId> {
  if (!isLeader()) {
    // Proxy to the owner tab, which owns the connection and originates the
    // call. The returned id is a placeholder — the real call surfaces via the
    // mirror and no caller consumes this value.
    phoneBus.postCommand({
      cmd: 'dial',
      payload: { number, brand: String(brand), customer: customer ?? null, context: context ?? null },
    });
    return crypto.randomUUID();
  }
  return dialLocal(number, brand, customer, context);
}

async function dialLocal(
  number: string,
  brand: Brand | string,
  customer?: CallerInfo,
  context?: DialContext,
): Promise<ClientCallId> {
  if (!userId) throw new Error('Telephony not initialised');

  // Eén-lijns-softphone: een tweede uitgaande dial tijdens een levend gesprek
  // trok het gedeelde remote-audio-element onder het lopende gesprek vandaan
  // (incident 2026-07-07, 08:33 + 08:40: audioLevel → exact 0 direct na de
  // tweede dial). Guard op de owner (registry = waarheid): élk belpad komt
  // hier langs, ook proxied dials van volger-tabs; de fout mirror't via de
  // snapshot naar alle tabs (banner in de dock).
  const live = registry.all().find((e) => e.record.state !== 'ended');
  if (live) {
    const ringingInbound =
      live.record.direction === 'inbound' && live.record.answeredAt === null;
    emit({
      type: 'dial_blocked_active_call',
      source: 'client',
      clientCallId: live.record.clientCallId,
      metadata: { to: number, brand: String(brand), liveState: live.record.state },
    });
    setCallError(
      live.record.clientCallId,
      ringingInbound
        ? 'Niet gebeld: er staat nog een inkomend gesprek open. Neem eerst op of weiger het.'
        : 'Niet gebeld: je bent nog in gesprek. Beëindig het huidige gesprek eerst.',
    );
    throw new DialBlockedError();
  }

  // Make sure the WebRTC socket is registered so the server-created A-leg
  // can actually reach us as an inbound invite.
  await ensureReady();

  const clientCallId = crypto.randomUUID();
  const toE164 = normaliseToE164(number);
  const brandKey = (brand || 'CINCO').toUpperCase() as Brand;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  emit({
    type: 'dial_attempted',
    source: 'client',
    clientCallId,
    metadata: { to: toE164, brand: brandKey },
  });

  // Track BEFORE the POST: Telnyx may dial the browser (and fire a
  // notification) before our fetch response resolves, because the server
  // does several DB writes after createTexmlCall returns. If we tracked
  // after the fetch, the handleNotification bridge-match would see an
  // empty pending map → call rings as a normal inbound.
  pendingOutbound.track({
    clientCallId,
    pstnTo: toE164,
    brand: brandKey,
    customer: customer ?? null,
    startedAt: Date.now(),
  });

  // Server creates the A-leg via Telnyx TeXML REST Calls API.
  // handleNotification consumes the pending entry on the inbound
  // bridge-leg and auto-answers without a UI ring.
  const res = await fetch('/api/telephony/call', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      to: toE164,
      brand: brandKey,
      customerId: customer?.id,
      customerName: customer?.name,
      clientCallId,
      // Belcontext (terugbel-lus, 22-10): taak/order/reden naar calls.context_*.
      context: context ?? null,
    }),
  });

  if (!res.ok) {
    pendingOutbound.consumeByClientCallId(clientCallId);
    const err = await res.json().catch(() => ({ error: 'Call failed' }));
    emit({
      type: 'dial_rejected',
      source: 'server',
      clientCallId,
      metadata: { status: res.status, error: err.error },
    });
    throw new Error(err.error || 'Call failed');
  }

  const data = (await res.json()) as {
    callId?: string;
    conversationId?: string;
    fromNumber?: string;
  };

  // Seed the registry now so the UI has a visible record while we wait for
  // the server-originated A-leg notification. telnyxCallId is attached when
  // that notification arrives.
  const record: CallRecord = {
    clientCallId,
    callId: data.callId ?? null,
    telnyxCallId: null,
    conversationId: data.conversationId ?? null,
    direction: 'outbound',
    state: 'idle',
    brand: brandKey,
    remoteNumber: toE164,
    startedAt: new Date(),
    answeredAt: null,
    endedAt: null,
    customer: customer ?? null,
    recoveredFromCallId: null,
    holdSince: null,
    transferState: null,
  };

  registry.add(record);
  registry.send(clientCallId, { type: 'DIAL_REQUESTED' });

  // A-leg-vóór-POST-race: kwam de bridge-leg-notificatie al binnen voordat
  // dit record bestond, dan staat de telnyxCallId geparkeerd in earlyLegs.
  const earlyTelnyxId = earlyLegs.get(clientCallId);
  if (earlyTelnyxId) {
    earlyLegs.delete(clientCallId);
    registry.update(clientCallId, { telnyxCallId: earlyTelnyxId });
  }

  return clientCallId;
}

// ─── Per-call operations (leader-aware) ────────────────────────────
//
// Public functions are leader-aware: on a follower they proxy the action to
// the owner over the phone-bus; on the owner they run the *Local body. UI
// components call these without knowing which tab they're on.

export function answer(clientCallId: ClientCallId): void {
  if (!isLeader()) return phoneBus.postCommand({ cmd: 'answer', payload: { clientCallId } });
  answerLocal(clientCallId);
}

export function reject(clientCallId: ClientCallId): void {
  if (!isLeader()) return phoneBus.postCommand({ cmd: 'reject', payload: { clientCallId } });
  rejectLocal(clientCallId);
}

export function hangup(clientCallId: ClientCallId): void {
  if (!isLeader()) return phoneBus.postCommand({ cmd: 'hangup', payload: { clientCallId } });
  hangupLocal(clientCallId);
}

export function sendDTMF(clientCallId: ClientCallId, digit: string): void {
  if (!isLeader()) return phoneBus.postCommand({ cmd: 'dtmf', payload: { clientCallId, digit } });
  sendDTMFLocal(clientCallId, digit);
}

export function toggleMute(clientCallId: ClientCallId): void {
  if (!isLeader()) return phoneBus.postCommand({ cmd: 'mute-toggle', payload: { clientCallId } });
  toggleMuteLocal(clientCallId);
}

/**
 * Persist a device-preference change and apply it live on the owner tab.
 *
 * The connection (and thus mic constraints + the shared remote-audio element)
 * lives on the owner tab only. A change made on a follower tab is proxied over
 * the phone-bus so the owner updates its cache and applies the new device to
 * the SDK and any active call — without this, device changes only took effect
 * after a reload of the owner tab, and never mid-call.
 */
export function updateDevicePreferences(patch: Partial<DevicePreferences>): void {
  // Persist (Supabase + lokale cache van deze tab).
  void savePreferences(patch);
  // Altijd broadcasten — óók vanaf de owner: elke andere tab (owner of
  // volger) moet z'n cache verversen, anders rinkelt een stale tab op het
  // oude apparaat zodra die de toon-lock wint.
  phoneBus.postCommand({ cmd: 'set-devices', payload: patch });
  if (isLeader()) applyDeviceSettingsLocal(patch);
}

function applyDeviceSettingsLocal(patch: Partial<DevicePreferences>): void {
  if ('speakerId' in patch) {
    // Shared element sink volgt de cache; werkt ook mid-call.
    applyRemoteSpeakerSink();
  }
  if ('microphoneId' in patch) {
    const micId = patch.microphoneId ?? undefined;
    const client = sharedClient as any;
    if (client?.setAudioSettings) {
      Promise.resolve(
        client.setAudioSettings({
          micId,
          micLabel: patch.microphoneLabel ?? undefined,
        }),
      ).catch((err: unknown) => {
        console.warn('[telephony/client] setAudioSettings failed', err);
      });
    }
    // Actieve gesprekken live omschakelen naar de nieuwe microfoon.
    if (micId) {
      sdkCalls.forEach((rawCall) => {
        const call = rawCall as any;
        if (typeof call?.setAudioInDevice === 'function') {
          Promise.resolve(call.setAudioInDevice(micId)).catch((err: unknown) => {
            console.warn('[telephony/client] setAudioInDevice failed', err);
          });
        }
      });
    }
  }
}

/** Dismiss the post-call disposition form on every tab. */
export function clearDisposition(): void {
  if (!isLeader()) return phoneBus.postCommand({ cmd: 'clear-disposition', payload: {} });
  clearDispositionLocal();
}

function clearDispositionLocal(): void {
  if (!dispositionRecord) return;
  dispositionRecord = null;
  publishSnapshot();
}

function answerLocal(clientCallId: ClientCallId): void {
  console.info('[telephony/client] answer called for', clientCallId);
  // Vangnet naast de busy-suppressie (die dekt het 60s-stale-routeringsvenster
  // niet volledig): nooit een tweede gesprek aannemen naast een andere levende
  // lijn. De uitgaande A-leg zou anders op de achtergrond doorlopen en met dit
  // gesprek om het gedeelde remote-audio-element vechten (klasse-incident
  // 2026-07-07).
  if (hasLiveCall(clientCallId)) {
    console.warn('[telephony/client] answer geweigerd: andere lijn nog bezet', clientCallId);
    setCallError(
      clientCallId,
      'Aannemen geblokkeerd: je bent nog in gesprek. Beëindig het huidige gesprek eerst.',
    );
    emit({ type: 'answer_failed', source: 'client', clientCallId, metadata: { reason: 'line_busy' } });
    return;
  }
  const sdkCall = sdkCalls.get(clientCallId) as any;
  if (!sdkCall) {
    console.warn('[telephony/client] answer: no SDK call for', clientCallId, 'known:', Array.from(sdkCalls.keys()));
    // Zichtbaar falen: zonder dit bleef de UI stil op "rinkelt" hangen.
    setCallError(clientCallId, 'Aannemen mislukt: geen actieve verbinding voor dit gesprek. Probeer opnieuw of herlaad de pagina.');
    emit({ type: 'answer_failed', source: 'client', clientCallId, metadata: { reason: 'no_sdk_call' } });
    return;
  }
  console.info('[telephony/client] answer: invoking sdkCall.answer(), sdk state:', sdkCall.state);
  try {
    sdkCall.answer();
  } catch (err) {
    console.error('[telephony/client] sdkCall.answer() threw', err);
    setCallError(clientCallId, 'Aannemen mislukt. Probeer opnieuw.');
    emit({
      type: 'answer_failed',
      source: 'client',
      clientCallId,
      metadata: { reason: err instanceof Error ? err.message : String(err) },
    });
    return;
  }
  clearCallError();
  registry.send(clientCallId, { type: 'ANSWERED' });
  emit({ type: 'answered', source: 'client', clientCallId });
  void stampAgentLeg(clientCallId, sdkCall);
}

/**
 * Stamp de eigen (agent-)leg-id op de calls-rij zodat de server bij live
 * meeluisteren (supervise, fase B) beide legs van het gesprek kent. Inbound
 * kent de server alleen de klant-leg (TeXML parent); dit is de enige plek
 * waar de browser-leg-id beschikbaar is (SDK telnyxIDs). Best-effort: wacht
 * kort op de Realtime-merge die record.callId (db-id) zet.
 */
async function stampAgentLeg(clientCallId: ClientCallId, sdkCall: any): Promise<void> {
  const agentCallControlId: string | undefined =
    sdkCall?.telnyxIDs?.telnyxCallControlId ?? sdkCall?.options?.telnyxCallControlId;
  if (!agentCallControlId) return;

  for (let attempt = 0; attempt < 10; attempt++) {
    const callId = registry.get(clientCallId)?.record.callId;
    if (callId) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        await fetch('/api/telephony/call', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ callId, agentCallControlId }),
        });
      } catch (err) {
        console.warn('[telephony/client] agent-leg stamp failed', err);
      }
      return;
    }
    await new Promise((r) => setTimeout(r, 1000));
  }
}

function rejectLocal(clientCallId: ClientCallId): void {
  const sdkCall = sdkCalls.get(clientCallId) as any;
  try {
    sdkCall?.hangup();
  } catch {
    /* ignore */
  }
  finalizeCall(clientCallId, 'rejected_by_user', 'client');
}

function hangupLocal(clientCallId: ClientCallId): void {
  const sdkCall = sdkCalls.get(clientCallId) as any;
  const entry = registry.get(clientCallId);

  if (!sdkCall && entry?.record.direction === 'outbound') {
    // Annuleren tijdens het kiezen: de server-side A-leg bestaat (of is in
    // aanmaak) maar de browser-poot is nog niet gearriveerd. Lokaal ophangen
    // deed hier voorheen niets — de klant werd alsnog gebeld. Consumeer de
    // pending-entry (blokkeert late auto-answer) en hang de call server-side
    // op. De ending→ended-timeout van de machine ruimt het record op.
    pendingOutbound.consumeByClientCallId(clientCallId);
    void cancelServerSide(clientCallId);
    registry.send(clientCallId, { type: 'HANGUP_REQUESTED' });
    emit({ type: 'hangup', source: 'client', clientCallId, metadata: { serverCancel: true } });
    return;
  }

  try {
    sdkCall?.hangup();
  } catch {
    /* ignore */
  }
  registry.send(clientCallId, { type: 'HANGUP_REQUESTED' });
  emit({ type: 'hangup', source: 'client', clientCallId });
}

async function cancelServerSide(clientCallId: ClientCallId): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    await fetch('/api/telephony/call', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ clientCallId }),
    });
  } catch (err) {
    console.warn('[telephony/client] server-side cancel failed', err);
  }
}

function sendDTMFLocal(clientCallId: ClientCallId, digit: string): void {
  const sdkCall = sdkCalls.get(clientCallId) as any;
  if (!sdkCall) {
    console.warn('[telephony/client] sendDTMF: no SDK call for', clientCallId);
    return;
  }
  if (!/^[0-9*#]$/.test(digit)) {
    console.warn('[telephony/client] sendDTMF: invalid digit', digit);
    return;
  }
  try {
    sdkCall.dtmf(digit);
  } catch (err) {
    console.error('[telephony/client] sendDTMF failed', err);
    return;
  }
  emit({
    type: 'dtmf_sent',
    source: 'client',
    clientCallId,
    metadata: { digit },
  });
}

function toggleMuteLocal(clientCallId: ClientCallId): void {
  const sdkCall = sdkCalls.get(clientCallId) as any;
  if (!sdkCall) return;
  const next = !isMutedState;
  try {
    if (next) {
      sdkCall.muteAudio();
    } else {
      sdkCall.unmuteAudio();
    }
  } catch (err) {
    console.error('[telephony/client] toggleMute failed', err);
    return;
  }
  setMutedState(next);
  emit({
    type: next ? 'mute_on' : 'mute_off',
    source: 'client',
    clientCallId,
  });
}
